import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { selectChartShowThresholdColor } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { POPUP_MINIMIZED_WIDTH_SIZE } from '@app/main/main-content/floating-popup-manager/configs/floating-popup.config';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FpLoggerNoiseAiPredictorStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor/fp-logger-noise-ai-predictor.store';
import { FpLoggerNoiseAiPredictorChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-ai-predictor/services/fp-logger-noise-ai-predictor-chart.service';
import { FloatingPopupBarChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-bar-chart-wrapper/floating-popup-bar-chart-wrapper.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-logger-noise-ai-predictor-minimized',
  standalone: true,
  imports: [ElementWidthClassDirective, FloatingPopupBarChartWrapperComponent, FloatingPopupBaseErrorMessageComponent],
  templateUrl: './fp-logger-noise-ai-predictor-minimized.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full',
  },
})
export class FpLoggerNoiseAiPredictorMinimizedComponent {
  public readonly currentTabStore = inject(FpLoggerNoiseAiPredictorStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpLoggerNoiseAiPredictorChartService);
  private readonly store = inject(Store);

  public popup = input<IFloatingPopupInstance>();

  public readonly POPUP_MINIMIZED_WIDTH_SIZE = POPUP_MINIMIZED_WIDTH_SIZE;

  private readonly chartShowThresholdColor = toSignal(
    this.store.select(selectChartShowThresholdColor).pipe(map(value => !!value)),
    { initialValue: false }
  );

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);

  public readonly chartPlugins = [this.chartService.getBottomCirclesPlugin()];

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const showThresholdColor = this.chartShowThresholdColor();

    const chartData = this.chartService.getMinimizedDataForChart(data, { showThresholdColor });
    const chartOptions = this.chartService.generateMinimizedOptions();

    return { chartData, chartOptions };
  });
}
